import React, { memo } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { TChartData, TMarkerPoint } from "@components/Charts/configs";
import BaseImage from "@components/BaseImage";
import VaultAreaChart from "./charts/VaultAreaChart";
import { TimeframeOption } from "@constants/types";
import { trackEvent } from "utils";
import { VAULT_PERFORMANCE_TIMEFRAME_CHANGE } from "@constants/analytics";

interface ChartAreaProps {
  chartData?: TChartData & {
    selectedTimeframeApr?: number | null;
    aprLabel?: string;
  };
  markerPoints: TMarkerPoint[];
  showTimeframeSelector?: boolean;
  onTimeframeChange?: (timeframe: TimeframeOption) => void;
  tokenType: "agETH" | "hgETH" | "sbUSD";
  aprTooltip?: string;
  activeTimeframe?: TimeframeOption;
}

// Timeframe selector component
const TimeframeSelector = memo(
  ({
    activeTimeframe,
    onChange,
    tokenType,
  }: {
    activeTimeframe: TimeframeOption;
    onChange: (timeframe: TimeframeOption) => void;
    tokenType: "agETH" | "hgETH" | "sbUSD";
  }) => {
    // Show 7D only for sbUSD, otherwise start from 1M
    const timeframes: TimeframeOption[] =
      tokenType === "sbUSD" ? ["7D", "1M"] : ["7D", "1M", "3M", "6M"];

    return (
      <div className="inline-flex items-center gap-2 rounded-lg bg-bg-primary border border-border-default border-1">
        {timeframes.map((timeframe) => (
          <button
            key={timeframe}
            onClick={() => onChange(timeframe)}
            className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
              activeTimeframe === timeframe
                ? "text-text-primary bg-white font-regular"
                : "text-text-tertiary hover:text-text-primary"
            }`}
          >
            {timeframe}
          </button>
        ))}
      </div>
    );
  }
);

TimeframeSelector.displayName = "TimeframeSelector";

// APR Display component
const AprDisplay = memo(
  ({
    apr,
    label,
  }: {
    apr: number | null | undefined;
    label: string;
    tooltipText?: string;
  }) => {
    if (apr === null || apr === undefined) return null;

    return (
      <Flex direction="column" gap={1}>
        <Text
          fontSize={{ base: "20px", md: "24px", lg: "28px" }}
          fontWeight="500"
          color="#1A1A1A"
          lineHeight="1"
        >
          {apr.toFixed(2)}{" "}
          <span style={{ fontSize: "20px", fontWeight: "400" }}>%</span>
        </Text>
        <Flex gap={1} alignItems="center">
          <Text
            fontSize="12px"
            color="textSecondary"
            fontWeight="400"
            lineHeight="1.2"
          >
            {label}
          </Text>
        </Flex>
      </Flex>
    );
  }
);

AprDisplay.displayName = "AprDisplay";

// Placeholder component for when no chart data is available
const ChartPlaceholder = memo(() => (
  <div className="relative rounded-xl">
    <div className="relative blur-md pointer-events-none opacity-60 h-[330px] sm:h-[380px] tablet:h-[340px] overflow-hidden">
      <BaseImage
        src="/assets/vaults/blurred_chart.svg"
        alt="placeholder chart"
        width={100}
        height={100}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] flex items-center justify-center text-center w-full">
      <span className="text-sm tablet:text-base w-full">
        Performance charts coming soon
      </span>
    </div>
  </div>
));

ChartPlaceholder.displayName = "ChartPlaceholder";

// Main chart area component - consolidated from ChartArea and PerformanceChart
const ChartArea = memo(function ChartArea({
  chartData,
  markerPoints,
  showTimeframeSelector = true,
  onTimeframeChange,
  tokenType,
  aprTooltip,
  activeTimeframe = "1M",
}: ChartAreaProps) {
  const handleTimeframeChange = (timeframe: TimeframeOption) => {
    onTimeframeChange?.(timeframe);
    trackEvent(VAULT_PERFORMANCE_TIMEFRAME_CHANGE, {
      vault_type: tokenType,
      timeframe: timeframe,
    });
  };

  // Show placeholder if no chart data
  if (!chartData) {
    return <ChartPlaceholder />;
  }

  const { selectedTimeframeApr, aprLabel } = chartData;

  return (
    <div className="w-full">
      <Flex direction={"column"}>
        {/* Header with APR and Timeframe Selector */}
        {showTimeframeSelector && (
          <Flex
            direction={{ base: "column", sm: "row" }}
            justify={{ base: "flex-start", sm: "space-between" }}
            align={{ base: "flex-start", sm: "center" }}
            mb={3}
            gap={{ base: 3, md: 0 }}
            px={2}
            pt={3}
          >
            {/* APR Display */}
            <AprDisplay
              apr={selectedTimeframeApr}
              label={aprLabel ?? ""}
              tooltipText={aprTooltip}
            />
            {/* Timeframe Selector */}
            <TimeframeSelector
              activeTimeframe={activeTimeframe}
              onChange={handleTimeframeChange}
              tokenType={tokenType}
            />
          </Flex>
        )}
        <Box
          className="relative"
          height={{ base: 330, sm: 380, tablet: 340 }}
          minHeight={{ base: 330, sm: 380, tablet: 340 }}
        >
          <VaultAreaChart
            chartData={chartData}
            markerPoints={markerPoints}
            tokenType={tokenType}
          />
        </Box>
      </Flex>
    </div>
  );
});

export default ChartArea;
